/**
 * HTML 태그 제거 + 엔티티 디코딩.
 *
 * ⚠️ 리포에 판본이 둘이고 **결과가 달라 합치지 않는다**:
 *  · 이것(정본)  이름 엔티티 6종 + 10진(`&#54620;`)·16진(`&#xD55C;`) 엔티티
 *    + `\uXXXX`·`\xNN` 이스케이프까지 디코딩하고, 끝에 연속 공백을 접고 trim 한다.
 *  · `utils/moments/domain/moment-ingest.ts` 의 `removeHtmlTagsFromText`
 *    태그 정규식이 `/<\/?[a-zA-Z][^>]*>/g` 라 이모티콘의 `>_<` 를 살린다. 공백 접기·trim 없음.
 */

/**
 * HTML 태그를 제거하고 순수 텍스트만 추출
 * @param {string} html - HTML이 포함된 문자열
 * @returns {string} HTML 태그가 제거된 순수 텍스트
 * 
 * @example
 * removeHtmlTags('<p>안녕하세요 <strong>세계</strong>!</p>') // "안녕하세요 세계!"
 * removeHtmlTags('<div>텍스트</div>') // "텍스트"
 * removeHtmlTags('순수 텍스트') // "순수 텍스트"
 */
export const removeHtmlTags = (html: unknown): string => {
  if (!html || typeof html !== 'string') return '';

  // HTML 태그 제거
  const withoutTags = html.replace(/<[^>]*>/g, '');

  // HTML 엔티티 디코딩
  const decoded = withoutTags
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    // 숫자 엔티티 (예: &#54620; = 한)
    .replace(/&#(\d+);/g, (_, code) => {
      const n = Number(code);
      if (!Number.isFinite(n) || n <= 0) return _;
      try {
        return String.fromCodePoint(n);
      } catch {
        return _;
      }
    })
    // 16진 엔티티 (예: &#xD55C; = 한)
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => {
      const n = parseInt(hex, 16);
      if (!Number.isFinite(n) || n <= 0) return _;
      try {
        return String.fromCodePoint(n);
      } catch {
        return _;
      }
    })
    // 백슬래시 유니코드 이스케이프 (예: \\uD55C\\uAE00 = 한글)
    // 데이터가 JSON-escaped 상태로 들어오는 케이스 대응
    .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => {
      const n = parseInt(hex, 16);
      if (!Number.isFinite(n)) return _;
      try {
        return String.fromCharCode(n);
      } catch {
        return _;
      }
    })
    // \\xNN (라틴/기호)
    .replace(/\\x([0-9a-fA-F]{2})/g, (_, hex) => {
      const n = parseInt(hex, 16);
      if (!Number.isFinite(n)) return _;
      try {
        return String.fromCharCode(n);
      } catch {
        return _;
      }
    });

  // 연속된 공백을 하나로 치환하고 앞뒤 공백 제거
  return decoded.replace(/\s+/g, ' ').trim();
};

/**
 * 글 본문을 `white-space: pre-line` 으로 그릴 꼴로 — 줄 바꿈 태그(`<br>`·`<p>`·`<pre>`)만 개행으로 바꾸고 `<code>` 껍데기를 벗긴다.
 *
 * 🔑 **태그를 걷지 않는다** — 걷고 나서 엔티티를 푸는 함수(`removeHtmlTags` 계열)는 태그를 되살린다.
 *   세척은 렌더 경계의 `SafeHtml`(DOMPurify)이 한다. 이 함수의 몫은 개행 변환뿐이다.
 * @returns 앞뒤 공백을 걷은 문자열 · 값이 없으면 빈 문자열
 */
export const toPreLineHtml = (raw: unknown): string =>
  String(raw || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<p[^>]*>/gi, '\n')
    .replace(/<\/p>/gi, '')
    .replace(/<pre[^>]*>/gi, '\n')
    .replace(/<\/pre>/gi, '\n')
    .replace(/<code[^>]*>/gi, '')
    .replace(/<\/code>/gi, '')
    .trim();
